<script setup>
// 活動辦法：對應設計稿「活動辦法-活動介紹」「活動辦法-投票規則與獎項」與
// 「活動辦法-主題形象蓋章規則」三張圖。三張稿是同一頁的三個分頁，共用同一張橫幅、
// 同一組背景裝飾與同一套區塊樣式，因此做成單一頁面 + 分頁切換，而不是三支路由。
//
// 內容取自 /front/activity/rules（公開介面，SSR 期間就取回）：活動名稱、起訖日期、
// 後台維護的五段富文本說明（含蓋章規則）與獎品清單。介面掛掉或系統還沒有活動時，
// 一律退回設計稿原本的假文案，版面不會開天窗。
import banner from '~/assets/img/banner/banner1.png'
import bannerBg from '~/assets/img/rules-banner-bg.png'
import megaphone from '~/assets/img/rules-banner-megaphone.png'
import sparkle from '~/assets/img/rules-banner-sparkle.png'
import heartWhite from '~/assets/img/rules-banner-heart-white.png'
import heartRed from '~/assets/img/rules-banner-heart-red.png'
import starFace from '~/assets/img/rules-banner-star-face.png'
import goGo from '~/assets/img/rules-banner-gogo.png'
import timelineCard from '~/assets/img/rules-timeline-card.png'
import bgMap from '~/assets/img/rules-bg-map.png'
import bgSun from '~/assets/img/rules-bg-sun.png'
import prizeStar from '~/assets/img/rules-prize-star.png'
import award2 from '~/assets/img/rules-award-2.png'
import award3 from '~/assets/img/rules-award-3.png'
import award4 from '~/assets/img/rules-award-4.png'
import award5 from '~/assets/img/rules-award-5.png'
import bonusTagBg from '~/assets/img/rules-bonus-tag.png'

definePageMeta({ layout: false })

const config = useRuntimeConfig()
useHead({ title: config.public.siteName })

const TABS = [
  { key: 'intro', label: '活動介紹' },
  { key: 'rules', label: '投票規則與獎項' },
  { key: 'stamp', label: '主題形象蓋章規則' }
]

// 分頁狀態同步到網址，「投票規則與獎項」才能被直接連結，重整也還停在同一頁
const route = useRoute()
const router = useRouter()
const tab = ref(TABS.some((t) => t.key === route.query.tab) ? route.query.tab : 'intro')

function switchTab(key) {
  if (tab.value === key) return
  tab.value = key
  router.replace({ query: key === 'intro' ? {} : { tab: key } })
}

/* ---------------- 資料來源 ---------------- */

const api = useApi()
const { resolveFileUrl } = useFileUrl()

const { data: page } = await useAsyncData('activity-rules', () =>
  api.get('/front/activity/rules').catch(() => null)
)

const activity = computed(() => page.value?.activity || null)

// 富文本清空後常留下 <p><br></p> 這類殘骸，單看字串長度會誤判成有內容，
// 因此改看去掉標籤後的文字；只放圖片也算有內容
function richOr(html, fallback) {
  const raw = String(html || '')
  const text = raw
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim()
  return text || /<img\b/i.test(raw) ? raw : fallback
}

const paras = (list) => list.map((t) => `<p>${t}</p>`).join('')
const orderedList = (list) => `<ol>${list.map((t) => `<li>${t}</li>`).join('')}</ol>`

/* ---------------- 橫幅與時程 ---------------- */

const FALLBACK_NAME = '115年全國登山步道票選活動推廣計畫'

const activityName = computed(() => activity.value?.activity_name || FALLBACK_NAME)
const subText = computed(() => `${activityName.value} — 投票資格、規則、抽獎機制與獎項一次看`)

// 時程只印票選的起訖兩個日期：設計稿拿掉了「票選結果公佈」，公佈日期改由中獎名單頁交代
const TIMELINE_LABELS = ['網路票選開始', '網路票選結束']
const FALLBACK_DATES = ['2026-09-20', '2026-10-16']

const timeline = computed(() => {
  const a = activity.value
  const dates = [a?.start_date, a?.end_date]
  return TIMELINE_LABELS.map((label, i) => ({
    label,
    date: dates[i] ? String(dates[i]).slice(0, 10) : FALLBACK_DATES[i]
  }))
})

const dailyQuota = { num: '4票', label: '每人每日票數' }

/* ---------------- 活動介紹 ---------------- */

const FALLBACK_ABOUT = paras([])

const aboutHtml = computed(() => richOr(activity.value?.about_content, FALLBACK_ABOUT))

/* ---------------- 投票規則與獎項 ---------------- */

const FALLBACK_QUALIFY = orderedList([])

const FALLBACK_RULE = orderedList([])

const FALLBACK_NOTICE = orderedList([])

const qualifyHtml = computed(() => richOr(activity.value?.qualify_content, FALLBACK_QUALIFY))
const ruleHtml = computed(() => richOr(activity.value?.rule_content, FALLBACK_RULE))
const noticeHtml = computed(() => richOr(activity.value?.notice_content, FALLBACK_NOTICE))

// 投票抽獎規則照設計稿寫死（抽獎次數本來就寫死在後端 utils/voteConst.js，後台沒有設定）。
// 規則有變動時直接改這裡——勤投加碼禮的活動期間也是寫死的，換一檔活動要記得一起改。
// 標題拆成數段，hl 為設計稿的橘色強調字；段落裡的 \n 就是設計稿的斷行位置。
// 文案的空格與半形標點照設計稿原樣保留。條列裡的 \n 是設計稿的換行點：網頁字型與稿子的
// 字寬不同，只調卡片寬度對不齊每一行（有的句子要放得下、同寬的另一句卻要換行），
// 因此直接寫明斷點；手機單欄時斷點不作用，交給瀏覽器自動換行
const bonuses = [
  {
    key: 'vote',
    tag: '票票有好禮',
    title: [{ text: '投票越多，抽獎次數越多\n' }, { text: '中獎機會越高！', hl: true }],
    points: [
      '每完成 1次有效投票，即可獲得 1\n次抽獎機會',
      '抽獎機會可隨投票次數累積',
      '每日最多可投8 票（基本 4 票 + 分\n享加碼送4票）',
      '每人每日最多可累積8次抽獎機會!'
    ]
  },
  {
    key: 'invite',
    tag: '揪團投票禮',
    title: [{ text: '成功邀請好友 ,' }, { text: '前 500 名直接送!', hl: true }],
    points: [
      '每位投票者皆有一組「專屬邀請\n碼」,可分享給親朋好友~',
      '讓好友於登入時輸入邀請碼，\n即視為邀請成功~',
      '前500 名成功邀請者，每人可獲得\n50 元數位商品禮券 ~',
      '每位邀請人限領一次 ,發放完畢為止'
    ]
  },
  {
    key: 'ranking',
    tag: '勤投加碼禮',
    title: [
      {
        text: '活動期間(115.09.24~115.10.20)\n依參與者「累積有效投票總數」進行排名，獎勵名額共計 305名'
      }
    ],
    points: [
      '第1至50名：每人獲得 300 元禮\n券，共50名。',
      '第51至100名：每人獲得 200元禮\n券，共50名。',
      '第101 至300名：每人獲得 100元禮\n券 , 共 200名。',
      '第 301 名後之所有參與者抽500\n元，共5名'
    ]
  }
]

// 獎品不退回設計稿的假資料：獎品是承諾送出去的東西，塞假品項會誤導使用者，
// 後台沒建獎品時整段「獎項設置」直接不顯示
const prizes = computed(() =>
  (page.value?.prizes || []).map((p) => ({
    key: p.id,
    level: p.prize_level,
    name: p.prize_name,
    qty: `共 ${p.quantity} ${p.unit || '個'}`,
    images: (p.images || []).map(resolveFileUrl)
  }))
)

// 設計稿排成 3 + 4 兩列，資料因此也切成兩組（後台的排序決定誰在第一列）
const prizesTop = computed(() => prizes.value.slice(0, 3))
const prizesRest = computed(() => prizes.value.slice(3))

/* ---------------- 主題形象蓋章規則 ---------------- */

// 蓋章規則是後台維護的富文本（stamp_content）；這份設計稿文案只在後台還沒填時頂上，
// 既有活動的欄位可能是 null
const FALLBACK_STAMP = orderedList([
  '每人每日共有 40 次蓋章機會，四大主題各提供 10 枚數位章。',
  '同一主題的數位章，同一條步道每日最多蓋 1 枚；因此，同一主題的 10 枚數位章須分配給 10 條不同步道。',
  '同一條步道可以獲得不同主題的數位章，因此每條步道每日最多可獲得 4 種不同主題章。',
  '民眾可依據各步道特色，自由決定不同主題數位章要支持哪些步道。',
  '投票期間公開各步道即時蓋章數與排名，讓民眾即時掌握各步道的支持情形，增加活動參與感與互動性。',
  '活動結束後，四大主題將分別統計 20 條步道的累計蓋章次數，並依各主題累計結果作為獎項評選依據。',
  '每日完成數位章蓋章（不限制蓋章數量）並確認送出者，可額外取得 1 次抽獎機會。'
])

const stampHtml = computed(() => richOr(activity.value?.stamp_content, FALLBACK_STAMP))

// 主題頭銜照設計稿寫死：四個主題是固定的，後台沒有對應的維護欄位
const awards = [
  { art: award2, title: '全齡友善' },
  { art: award3, title: '親子共融' },
  { art: award4, title: '人文古蹟' },
  { art: award5, title: '自然生態' }
]
</script>

<template>
  <div class="rules">
    <div class="head-fix">
      <SiteHeader active="rules" />
    </div>

    <!-- 橫幅：風景是整張底圖，上面的星星、大聲公、愛心都是獨立素材，
         位置與尺寸一律換算自 1440 設計稿，縮放時整組等比跟著縮 -->
    <!-- <section class="banner" :style="{ backgroundImage: `url(${bannerBg})` }">
      <div class="stage">
        <img class="deco d-heart-a" :src="heartRed" alt="" aria-hidden="true" />
        <img class="deco d-star" :src="starFace" alt="" aria-hidden="true" />
        <img class="deco d-gogo" :src="goGo" alt="" aria-hidden="true" />
        <img class="deco d-spark" :src="sparkle" alt="" aria-hidden="true" />

        <h1 class="plaque">活動辦法</h1>
        <img class="deco d-horn" :src="megaphone" alt="" aria-hidden="true" />
        <p class="sub">{{ subText }}</p>
        <img class="deco d-heart-b" :src="heartWhite" alt="" aria-hidden="true" />
      </div>
    </section> -->
    <section>
      <img
        style="width: 100%; height: auto; display: block"
        :src="banner"
        alt=""
        aria-hidden="true"
      />
    </section>

    <main class="body">
      <img class="bg-map" :src="bgMap" alt="" aria-hidden="true" />
      <img class="bg-sun" :src="bgSun" alt="" aria-hidden="true" />

      <div class="wrap">
        <div class="tabs" role="tablist">
          <button
            v-for="t in TABS"
            :key="t.key"
            class="tab"
            :class="{ 'is-on': tab === t.key }"
            type="button"
            role="tab"
            :aria-selected="tab === t.key"
            @click="switchTab(t.key)"
          >
            {{ t.label }}
          </button>
        </div>

        <!-- key 綁 tab：切換時整塊重建，進場動畫才會重播一次 -->
        <div :key="tab" class="panel">
          <template v-if="tab === 'intro'">
            <!-- 時程卡：外框、菱格紋與吊牌都烘在 rules-timeline-card.png 裡，
                 這裡只負責把日期、時間軸與票數疊上去 -->
            <section class="sec tl-card" :style="{ '--tl-card-bg': `url(${timelineCard})` }">
              <div class="tl">
                <span class="tl-bar" aria-hidden="true"></span>
                <div v-for="item in timeline" :key="item.label" class="tl-item">
                  <span class="tl-date">{{ item.date }}</span>
                  <span class="tl-star"></span>
                  <span class="tl-label">{{ item.label }}</span>
                </div>
              </div>
              <div class="tl-quota">
                <strong>{{ dailyQuota.num }}</strong>
                <span>{{ dailyQuota.label }}</span>
              </div>
            </section>

            <section class="sec">
              <div class="sec-head"><span class="sec-label">關於本活動</span></div>
              <div class="sec-box is-lime">
                <!-- 四段說明皆為後台維護的富文本，樣式統一由 .rich 收斂 -->
                <div class="sec-inner rich" v-html="aboutHtml"></div>
              </div>
            </section>
          </template>

          <template v-else-if="tab === 'rules'">
            <section class="sec">
              <div class="sec-head"><span class="sec-label">投票資格與方式</span></div>
              <div class="sec-box is-lime">
                <div class="sec-inner rich" v-html="qualifyHtml"></div>
              </div>
            </section>

            <section class="sec">
              <div class="sec-head"><span class="sec-label">投票規則(每人每日 4 票)</span></div>
              <div class="sec-box is-lime">
                <div class="sec-inner rich" v-html="ruleHtml"></div>
              </div>
            </section>

            <section class="sec">
              <div class="sec-head"><span class="sec-label">投票抽獎規則</span></div>
              <div class="sec-box is-aqua">
                <div class="bonus-grid">
                  <article v-for="b in bonuses" :key="b.key" class="bonus">
                    <span class="bonus-tag" :style="{ backgroundImage: `url(${bonusTagBg})` }">
                      <span class="bonus-tag-text">{{ b.tag }}</span>
                    </span>
                    <h3>
                      <span v-for="(seg, i) in b.title" :key="i" :class="{ 'is-hl': seg.hl }">{{
                        seg.text
                      }}</span>
                    </h3>
                    <ul class="bonus-points">
                      <li v-for="(pt, i) in b.points" :key="i">
                        <template v-for="(line, j) in pt.split('\n')" :key="j"
                          ><br v-if="j" class="bonus-br" />{{ line }}</template
                        >
                      </li>
                    </ul>
                  </article>
                </div>
              </div>
            </section>

            <section v-if="prizes.length" class="sec">
              <div class="sec-head">
                <span class="sec-label">獎項設置</span>
                <span class="sec-note">※ 獎品無法指定顏色或款式,實際樣式依主辦單位公告為準</span>
              </div>
              <div class="sec-box is-mint prize-box">
                <div class="prize-grid is-3">
                  <article v-for="(p, i) in prizesTop" :key="p.key" class="prize">
                    <div class="prize-badge">
                      <div class="prize-badge-img">
                        <img :src="prizeStar" alt="" aria-hidden="true" />
                      </div>
                      <span>{{ p.level }}</span>
                    </div>
                    <!-- 獎品圖在後台是必填，多張時輪播 -->
                    <div class="prize-art">
                      <ImageCarousel :images="p.images" :alt="p.name" :delay="3500 + i * 400" />
                    </div>
                    <h3>{{ p.name }}</h3>
                    <p>{{ p.qty }}</p>
                  </article>
                </div>
                <div v-if="prizesRest.length" class="prize-grid is-4">
                  <article v-for="(p, i) in prizesRest" :key="p.key" class="prize">
                    <div class="prize-badge">
                      <div class="prize-badge-img">
                        <img :src="prizeStar" alt="" aria-hidden="true" />
                      </div>
                      <span>{{ p.level }}</span>
                    </div>
                    <!-- 獎品圖在後台是必填，多張時輪播 -->
                    <div class="prize-art">
                      <ImageCarousel :images="p.images" :alt="p.name" :delay="5000 + i * 400" />
                    </div>
                    <h3>{{ p.name }}</h3>
                    <p>{{ p.qty }}</p>
                  </article>
                </div>
              </div>
            </section>

            <section class="sec">
              <div class="sec-head"><span class="sec-label">注意事項</span></div>
              <div class="sec-box is-lime">
                <div class="sec-inner rich" v-html="noticeHtml"></div>
              </div>
            </section>
          </template>

          <template v-else>
            <section class="sec">
              <div class="sec-head"><span class="sec-label">蓋章規則</span></div>
              <div class="sec-box is-lime">
                <div class="sec-inner rich" v-html="stampHtml"></div>
              </div>
            </section>

            <section class="sec">
              <div class="sec-head"><span class="sec-label">親山步道主題頭銜</span></div>
              <div class="sec-box is-mint">
                <div class="award-grid">
                  <article v-for="a in awards" :key="a.title" class="award">
                    <div class="award-art">
                      <img :src="a.art" alt="" aria-hidden="true" />
                    </div>
                    <h3>{{ a.title }}</h3>
                  </article>
                </div>
              </div>
            </section>
          </template>
        </div>
      </div>
    </main>

    <SiteFooter />
  </div>
</template>

<style scoped>
.rules {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: var(--site-font);
  color: var(--site-ink);
  background: #fff;
}

/* 頁首釘在畫面最上方，內容捲動時不跟著走 */
.head-fix {
  position: sticky;
  top: 0;
  z-index: 50;
}

/* ---------------- 橫幅 ---------------- */

/* 底圖就是 1440 × 300：1440 以上讓高度跟著寬度等比長高，圖才會整張露出來；
   1440 以下高度守住 300，改由左右裁切，天空與稜線就不會被切掉 */
.banner {
  position: relative;
  aspect-ratio: 1440 / 300;
  min-height: 300px;
  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;
  overflow: hidden;
}

/* 裝飾的座標都相對設計稿寬度：1440 以下舞台維持 1440 並置中溢出，
   跟著底圖一起被左右裁切；1440 以上撐滿寬度，與放大的底圖同步 */
.stage {
  position: absolute;
  top: 0;
  left: 50%;
  width: max(100%, 1440px);
  height: 100%;
  transform: translateX(-50%);
}

.deco {
  position: absolute;
  display: block;
  height: auto;
  pointer-events: none;
}

/* 星星右半邊本來就該被白色標題牌壓住，位置與尺寸照設計稿的 131px 原寸擺 */
.d-star {
  left: 16.95%;
  top: 21.7%;
  width: 9.1%;
}

/* 大聲公壓在牌子右緣上：牌子縮到設計稿寬度後，這個交界才對得起來 */
.d-horn {
  left: 47.65%;
  top: 20.3%;
  width: 9.6%;
}

.d-gogo {
  left: 54.1%;
  top: 8.7%;
  width: 8.68%;
}

.d-spark {
  left: 63.4%;
  top: 8.7%;
  width: 1.95%;
}

.d-heart-a {
  left: 13.9%;
  top: 10.7%;
  width: 1.53%;
}

.d-heart-b {
  left: 75.15%;
  top: 63%;
  width: 3%;
}

.d-heart-c {
  left: 77.8%;
  top: 62.3%;
  width: 2.08%;
}

/* 白色標題牌與底下的藍色說明條都帶一點傾斜，是設計稿的手作感 */
.plaque {
  position: absolute;
  left: 24.3%;
  top: 26.3%;
  width: 27.2%;
  height: 29.4%;
  margin: 0;
  display: flex;
  align-items: center;
  /* 標題靠左：牌子左緣壓在星星上，靠左才對得上設計稿的字距 */
  justify-content: flex-start;
  padding-left: max(48px, 3.33vw);
  background: #fff;
  color: #101010;
  font-size: max(70px, 4.86vw);
  font-weight: 900;
  line-height: 1;
  transform: rotate(-1deg);
  box-shadow: 0 6px 16px rgba(var(--site-shadow-rgb), 0.18);
}

.sub {
  position: absolute;
  left: 23.7%;
  top: 55%;
  width: 53%;
  height: 15.4%;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 max(12px, 0.84vw);
  background: #60d3f7;
  color: #ffd84a;
  /* 字級抓到剛好填滿藍條：Noto Sans TC 的全形字寬是 1em，再大一級就會滿出去 */
  font-size: max(19.5px, 1.35vw);
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  transform: rotate(-1deg);
  /* 黃字疊在淺藍上對比不足，設計稿是黑色描邊；用八向陰影模擬，
     -webkit-text-stroke 會往字身內縮，中文筆畫會糊成一團 */
  text-shadow:
    -2px 0 #111,
    2px 0 #111,
    0 -2px #111,
    0 2px #111,
    -2px -2px #111,
    2px -2px #111,
    -2px 2px #111,
    2px 2px #111;
}

/* ---------------- 內容區 ---------------- */

.body {
  position: relative;
  flex: 1;
  /* 底部要留給頁尾的山稜線疊上來：稜線是 1440 × 175 等比縮放，
     窄螢幕上只有幾十 px，用固定值會留下一大片空白 */
  padding: 40px 0 calc(min(12.15vw, 175px) + 40px);
  background: linear-gradient(180deg, #f4ffdb 0, #fdfff7 480px, #ffffff 900px);
  overflow: hidden;
}

.bg-map,
.bg-sun {
  position: absolute;
  display: block;
  pointer-events: none;
  user-select: none;
}

.bg-map {
  right: 0;
  top: 87px;
  width: 224px;
}

.bg-sun {
  left: 0;
  top: 431px;
  width: 301px;
}

.wrap {
  position: relative;
  z-index: 1;
  max-width: 940px;
  margin: 0 auto;
  padding: 0 20px;
}

/* ---------------- 分頁切換 ---------------- */

.tabs {
  display: flex;
  height: 56px;
  padding: 4px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(255, 205, 70, 0.45);
}

.tab {
  flex: 1;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #575757;
  font-family: inherit;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
  transition:
    background-color 0.25s,
    color 0.25s;
}

.tab:hover {
  color: var(--site-orange);
}

.tab.is-on {
  background: var(--site-orange);
  color: #fff;
}

.tab.is-on:hover {
  color: #fff;
}

.panel {
  animation: panelIn 0.5s cubic-bezier(0.16, 0.84, 0.32, 1) both;
}

@keyframes panelIn {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/* ---------------- 區塊外殼 ---------------- */

.sec {
  margin-top: 64px;
}

.sec:first-child {
  margin-top: 40px;
}

.sec-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  /* 黑色小標不齊左，是往右縮排一段坐在盒子肩上 */
  padding: 0 4px 0 46px;
}

.sec-label {
  display: inline-flex;
  align-items: center;
  height: 40px;
  padding: 0 14px;
  border-radius: 12px 12px 0 0;
  background: #000;
  color: #fffc47;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}

.sec-note {
  color: #4a4a4a;
  font-size: 14px;
  line-height: 1.6;
}

.sec-box {
  padding: 20px;
  border-radius: 20px;
}

.sec-box.is-lime {
  background: linear-gradient(180deg, #f8f2a1 0%, #b5fa73 100%);
}

.sec-box.is-mint {
  background: linear-gradient(180deg, #98f4f7 0%, #b2f970 100%);
}

.sec-box.is-aqua {
  padding-top: 30px;
  padding-bottom: 23px;
  background: linear-gradient(180deg, #98f4f7 0%, #b2f970 100%);
}

.sec-inner {
  padding: 26px 28px;
  border-radius: 18px;
  background: #fff;
}

/* 四段說明文案是後台富文本，標籤由編輯器決定，樣式在這裡統一收斂成內文排版 */
.rich {
  color: #000000;
  font-size: 16px;
  line-height: 1.65;
}

.rich :deep(p) {
  margin: 0 0 12px;
}

.rich :deep(ol),
.rich :deep(ul) {
  margin: 0 0 12px;
  padding-left: 26px;
  color: var(--site-text);
}

.rich :deep(li + li) {
  margin-top: 4px;
}

.rich :deep(h2) {
  margin: 20px 0 10px;
  font-size: 20px;
  font-weight: 700;
}

.rich :deep(h3) {
  margin: 18px 0 8px;
  font-size: 18px;
  font-weight: 700;
}

.rich :deep(a) {
  color: var(--site-orange-deep);
  text-decoration: underline;
}

.rich :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 12px;
}

.rich :deep(table) {
  width: 100%;
  margin: 0 0 12px;
  border-collapse: collapse;
}

.rich :deep(th),
.rich :deep(td) {
  padding: 8px 10px;
  border: 1px solid var(--site-line);
}

/* 收掉最後一段的下緣留白，白底卡片的上下內距才會對稱 */
.rich :deep(> *:last-child) {
  margin-bottom: 0;
}

/* ---------------- 活動介紹：時程卡 ---------------- */

/* 綁兩個 class 是為了壓過 .sec:first-child 的 margin，讓卡片自己決定上下留白 */
.sec.tl-card {
  position: relative;
  width: 760px;
  max-width: 100%;
  min-height: 418px;
  margin: 40px auto 0;
  padding: 105px 32px 40px;
  background-image: var(--tl-card-bg);
  background-position: center;
  background-size: 100% 100%;
  background-repeat: no-repeat;
}

/* 只剩起訖兩個日期，手機也排得下，因此一路維持橫向軸線、不再改成直排 */
.tl {
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}

/* 黃色軸線兩端比欄位再往內縮一點，才不會頂到卡片白底的圓角 */
.tl-bar {
  position: absolute;
  left: 22px;
  right: 22px;
  top: 50px;
  height: 18px;
  border: 2px solid #5fe0f5;
  border-radius: 999px;
  background: #ffdc3c;
}

.tl-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.tl-date {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 150px;
  height: 26px;
  padding: 0 42.5px;
  border-radius: 999px;
  background: #eb7128;
  border: 2px solid #ffffff;
  color: #fff;
  font-size: 18px;
  font-style: italic;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
}

/* 設計稿的結束日期是淺一階的橘，開始與結束一眼分得出來 */
.tl-item + .tl-item .tl-date {
  background: #ff8d28;
}

.tl-star {
  display: block;
  width: 26px;
  height: 26px;
  border: 2px solid #4be1f7;
  background: #eb7128;
  border-radius: 50%;
  margin: 19px 0 18px;
}

.tl-label {
  color: #282625;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}

.tl-quota {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 190px;
  height: 100px;
  margin: 41px auto 0;
  border-radius: 12px;
  background: #eb7128;
  color: #ffffff;
}

.tl-quota strong {
  font-size: 30px;
  font-weight: 700;
  line-height: 1.2;
}

.tl-quota span {
  font-size: 18px;
  line-height: 1.4;
}

/* ---------------- 主題形象蓋章規則 ---------------- */

/* 蓋章規則底下直接給入口，看完規則不必再回頁首找 */
.stamp-go {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

.stamp-go-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 200px;
  height: 52px;
  padding: 0 28px;
  border-radius: 999px;
  background: var(--site-orange);
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  transition:
    background-color 0.25s,
    box-shadow 0.25s;
}

.stamp-go-btn:hover {
  background: var(--site-orange-deep);
  box-shadow: 0 10px 22px rgba(var(--site-orange-rgb), 0.3);
}

.award-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
}

.award {
  padding: 16px 16px 24px;
  border-radius: 18px;
  background: #fff;
  text-align: center;
}

.award-art {
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1 / 1;
  margin-bottom: 20px;
  border-radius: 14px;
  /* background: linear-gradient(180deg, #ffe3ce 0%, #fff4e8 100%); */
}

/* 插圖本身是去背方圖，留一點內距免得緊貼底色邊界 */
.award-art img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.award h3 {
  margin: 0 0 8px;
  color: #2f2f2f;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

/* ---------------- 投票規則：加碼抽獎 ---------------- */

/* 三項加碼排成一行三張，每張卡等寬等高——2 × 2 會留一個空格，看起來像少了一項 */
.bonus-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px 19px;
}

.bonus {
  position: relative;
  min-height: 140px;
  padding: 57px 15px 19px;
  border-radius: 16px;
  background: #fff;
}

.bonus-tag {
  position: absolute;
  left: 0;
  top: -7px;
  display: inline-flex;
  align-items: center;
  /* 素材原寸 149 × 54：上面 38px 是色塊，下面是壓在白卡上的左下折角，
     所以高度照原寸給滿，再用 padding-bottom 把文字推回色塊中央 */
  height: 54px;
  min-width: 149px;
  padding: 0 34px 16px;
  background-repeat: no-repeat;
  /* 標籤寬度隨字數變動，素材直接拉滿；原寸就接近實際寬度，圓角形變看不出來 */
  background-size: 100% 100%;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}

/* 標籤底圖已佔用 background，漸層字色只能另開一層做 background-clip */
.bonus-tag-text {
  background: linear-gradient(90deg, #ffffff 0%, #fae362 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

/* 標題區固定三行高（第三張卡的標題最長）：三張卡的條列因此從同一個高度開始。
   行高量自設計稿：標題每行 24px，條列每行 20px */
.bonus h3 {
  min-height: 72px;
  margin: 0 0 12px;
  color: #000000;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  /* 標題裡的 
 就是設計稿的斷行位置 */
  white-space: pre-line;
}

.bonus h3 .is-hl {
  color: var(--site-orange);
}

.bonus-points {
  margin: 0;
  padding: 0;
  list-style: none;
  color: #3c4043;
  font-size: 14px;
  line-height: 20px;
}

/* 設計稿的項目符號是 3px 的小圓點、離文字很近，原生 disc 大一圈又離得遠，改自己畫 */
.bonus-points li {
  position: relative;
  padding-left: 21px;
}

.bonus-points li::before {
  content: '';
  position: absolute;
  left: 9px;
  top: 9px;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: currentColor;
}

/* ---------------- 投票規則：獎項設置 ---------------- */

.prize-grid {
  display: grid;
  gap: 18px;
}

.prize-grid.is-3 {
  grid-template-columns: repeat(3, 1fr);
}

.prize-grid.is-4 {
  grid-template-columns: repeat(4, 1fr);
  margin-top: 18px;
}

/* min-width: 0 必要：grid 項目預設 min-width: auto，裡面的 Swiper 依容器寬度排 slide，
   slide 又回頭把格子撐開，兩邊互相放大，第一張卡會撐滿整排 */
.prize {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 14px 14px 22px;
  border-radius: 16px;
  background: #fff;
  text-align: center;
}

.prize-badge {
  position: absolute;
  left: 10px;
  top: 10px;
  z-index: 99;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 30px;
  border-radius: 999px;
  background: #111;
}

.prize-badge span {
  padding-right: 24px;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
}

/* 白圓比黑膠囊大一圈、往上下各溢出 2px：兩者等高時圓弧完全重疊，
   黑底的反鋸齒邊會從白圓邊緣透出來，看起來就是一圈黑邊 */
.prize-badge-img {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  margin: -2px -2px;
  background: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.prize-badge-img img {
  width: 19px;
  height: 19px;
  object-fit: contain;
}

.prize-art {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  overflow: hidden;
  aspect-ratio: 1 / 1;
  margin-bottom: 18px;
  border-radius: 12px;
}

.prize h3 {
  margin: 0 0 10px;
  color: #2f2f2f;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
}

/* margin-top: auto 讓數量一律貼在卡片底部，品名兩行與一行的卡片才會對齊 */
.prize p {
  margin: auto 0 0;
  color: #3c4043;
  font-size: 14px;
  line-height: 1.5;
}

/* ---------------- 響應式 ---------------- */

@media (max-width: 1100px) {
  .bg-map,
  .bg-sun {
    display: none;
  }
}

@media (max-width: 900px) {
  /* 橫幅等比縮到手機只剩一百多 px，字會小到看不清；改成一般流排版置中 */
  .banner {
    aspect-ratio: auto;
    min-height: 0;
    height: auto;
    padding: 26px 16px 30px;
  }

  .stage {
    position: static;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: auto;
    min-width: 0;
    height: auto;
    transform: none;
  }

  .deco {
    display: none;
  }

  .plaque,
  .sub {
    position: static;
    width: auto;
    max-width: 100%;
    height: auto;
    transform: none;
  }

  .plaque {
    justify-content: center;
    padding: 10px 26px;
    font-size: 34px;
    letter-spacing: 3px;
  }

  .sub {
    padding: 8px 14px;
    font-size: 14px;
    line-height: 1.5;
    white-space: normal;
    text-align: center;
  }

  .body {
    padding-top: 28px;
  }

  .tabs {
    height: 48px;
  }

  .tab {
    font-size: 15px;
  }

  .sec {
    margin-top: 48px;
  }

  .sec-head {
    padding-left: 20px;
  }

  .sec-label {
    height: 34px;
    padding: 0 12px;
    font-size: 15px;
  }

  .sec-note {
    font-size: 12px;
  }

  .sec-box {
    padding: 14px;
    border-radius: 20px;
  }

  .sec-inner {
    padding: 18px 16px;
    border-radius: 14px;
  }

  .rich {
    font-size: 14px;
  }

  .rich :deep(ol),
  .rich :deep(ul) {
    padding-left: 22px;
  }

  .sec.tl-card {
    min-height: 0;
    margin-top: 28px;
    padding: 56px 16px 34px;
  }

  .tl-bar {
    left: 10px;
    right: 10px;
    top: 44px;
    height: 14px;
  }

  .tl-date {
    min-width: 0;
    height: 24px;
    padding: 0 8px;
    font-size: 12px;
  }

  .tl-star {
    width: 20px;
    height: 20px;
    margin: 15px 0 14px;
  }

  .tl-label {
    font-size: 14px;
  }

  .tl-quota {
    width: 150px;
    height: 84px;
    margin-top: 28px;
  }

  .tl-quota strong {
    font-size: 24px;
  }

  .tl-quota span {
    font-size: 14px;
  }

  .award-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  /* 3 + 4 兩組格線在手機併成同一條 2 欄流：分開排會變成 2 / 1 / 2 / 2，
     中間那張落單，看起來像斷成兩區 */
  .prize-box {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .prize-box .prize-grid {
    display: contents;
  }

  .award h3 {
    font-size: 16px;
  }

  .prize p {
    font-size: 13px;
  }

  .bonus-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .bonus {
    min-height: 0;
    padding: 30px 18px 20px;
  }

  .bonus-tag {
    top: -18px;
    height: 44px;
    padding: 0 16px 13px;
    font-size: 14px;
  }

  /* 一欄一張卡，沒有對齊的對象，標題不必再撐兩行高 */
  .bonus h3 {
    min-height: 0;
    margin-bottom: 12px;
    font-size: 16px;
  }

  .bonus-points {
    font-size: 13px;
  }

  /* 單欄時卡片變寬，設計稿的斷點會切出一截很短的行，改回自動換行 */
  .bonus-br {
    display: none;
  }

  /* 卡片變窄，徽章與留白跟著縮，圖片區才不會只剩一條縫 */
  .prize {
    padding: 10px 12px 16px;
  }

  .prize-badge {
    left: 8px;
    top: 8px;
    gap: 5px;
    height: 26px;
  }

  .prize-badge-img {
    width: 30px;
    height: 30px;
  }

  .prize-badge-img img {
    width: 17px;
    height: 17px;
  }

  .prize-badge span {
    padding-right: 12px;
    font-size: 12px;
  }

  .prize-art {
    margin-bottom: 12px;
  }

  .prize h3 {
    margin-bottom: 8px;
    font-size: 15px;
  }
}

@media (max-width: 700px) {
  /* 「獎項設置」右側的注意字樣一行放不下，改成疊在小標下方 */
  .sec-head {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
}

@media (max-width: 560px) {
  /* 三個分頁擠在一條膠囊裡，「主題形象蓋章規則」八個字要縮字級才放得下 */
  .tab {
    padding: 0 4px;
    font-size: 13px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .panel {
    animation: none;
  }
}
</style>
